import { onMounted, onUnmounted, ref, watch, toRaw, type Ref } from 'vue';
import { useUserStore } from '@/store';

/**
 * 用户信息弹窗 Hook
 *
 * 封装 LifangUserPanelPopover（来自 main.js）的创建与生命周期管理。
 * main.js API: new LifangUserPanelPopover({ triggerElement, userInfo })
 * - triggerElement: 触发弹窗的 DOM 元素
 * - userInfo: 用户数据（userData 中通过 ...data 保留了 aipicturebook_vip_type 等原始字段）
 * - 登出由组件内部处理（跳转 /api/user/sign-out）
 */
export const useUserPanelPopover = () => {
  const userStore = useUserStore();
  const triggerRef: Ref<HTMLElement | null> = ref(null);
  let popoverInstance: any = null;

  const initPopover = () => {
    if (!triggerRef.value) return;
    if (popoverInstance) return;
    if (!window.LifangUserPanelPopover) return;
    if (!userStore.isLogin) return;

    // Lifang 库 CSS 通过 #pb-head-avatar-trigger.c-user-panel-dashboard-trigger 匹配
    // 由 hook 统一注入，组件模板无需关心这些内部细节
    const el = triggerRef.value;
    el.setAttribute('id', 'pb-head-avatar-trigger');
    el.classList.add('c-user-panel-dashboard-trigger');

    popoverInstance = new window.LifangUserPanelPopover({
      triggerElement: el,
      userInfo: toRaw(userStore.userData)
    });
  };

  const destroyPopover = () => {
    popoverInstance?.destroy?.();
    popoverInstance = null;
  };

  onMounted(() => {
    initPopover();

    watch(
      () => userStore.isLogin,
      (loggedIn) => {
        if (loggedIn && !popoverInstance) initPopover();
        else if (!loggedIn) destroyPopover();
      }
    );
  });

  onUnmounted(() => {
    destroyPopover();
  });

  return { triggerRef };
};
